import React from 'react';
import _ from 'lodash';

import { enums } from 'console-kit';
import LightboxIcon from '../components/LightboxIcon';

const LIGHTBOX_EVENT_TYPE = {
  PAGE_LOAD:    'page-load',
  EXIT_INTENT:  'exit-intent',
  TIME_ON_PAGE: 'time-on-page',
  ACTIVE_TIME:  'active-time',
  IDLE_TIME:    'idle-time',
  SCROLL:       'scroll',
  CLICK:        'click',
  HOVER:        'hover',
  BUTTON:       'button',
};
LIGHTBOX_EVENT_TYPE.ALL = _.values(LIGHTBOX_EVENT_TYPE);

const DEFAULT_LIGHTBOX_TRANSPARENCY = 50;

const DEFAULT_INNETWORK_X_DIMENSION = 1600;
const DEFAULT_INNETWORK_Y_DIMENSION = 900;

const OVERLAY_SIZES = {
  SMALL:      'small',
  MEDIUM:     'medium',
  LARGE:      'large',
  FULLSCREEN: 'fullscreen',
};

const OVERLAY_SIZE_LIST = [
  { label: 'Small', value: OVERLAY_SIZES.SMALL },
  { label: 'Medium', value: OVERLAY_SIZES.MEDIUM },
  { label: 'Large', value: OVERLAY_SIZES.LARGE },
  { label: 'Full Screen', value: OVERLAY_SIZES.FULLSCREEN },
];

const DEFAULT_OVERLAY_SIZE = OVERLAY_SIZES.MEDIUM;

const DISPLAY_OVERLAY_POSITIONS = {
  TOP_LEFT:      'topLeft',
  TOP_CENTER:    'topCenter',
  TOP_RIGHT:     'topRight',
  CENTER_LEFT:   'centerLeft',
  CENTER:        'center',
  CENTER_RIGHT:  'centerRight',
  BOTTOM_LEFT:   'bottomLeft',
  BOTTOM_CENTER: 'bottomCenter',
  BOTTOM_RIGHT:  'bottomRight',
};

const OVERLAY_POSITIONS = {
  [DISPLAY_OVERLAY_POSITIONS.TOP_LEFT]:      enums.positions.inSquare.TOP_LEFT,
  [DISPLAY_OVERLAY_POSITIONS.TOP_CENTER]:    enums.positions.inSquare.TOP_CENTER,
  [DISPLAY_OVERLAY_POSITIONS.TOP_RIGHT]:     enums.positions.inSquare.TOP_RIGHT,
  [DISPLAY_OVERLAY_POSITIONS.CENTER_LEFT]:   enums.positions.inSquare.MIDDLE_LEFT,
  [DISPLAY_OVERLAY_POSITIONS.CENTER]:        enums.positions.inSquare.CENTERED,
  [DISPLAY_OVERLAY_POSITIONS.CENTER_RIGHT]:  enums.positions.inSquare.MIDDLE_RIGHT,
  [DISPLAY_OVERLAY_POSITIONS.BOTTOM_LEFT]:   enums.positions.inSquare.BOTTOM_LEFT,
  [DISPLAY_OVERLAY_POSITIONS.BOTTOM_CENTER]: enums.positions.inSquare.BOTTOM_CENTER,
  [DISPLAY_OVERLAY_POSITIONS.BOTTOM_RIGHT]:  enums.positions.inSquare.BOTTOM_RIGHT,
};

const DEFAULT_OVERLAY_POSITION = DISPLAY_OVERLAY_POSITIONS.CENTER;

const EVENT_TYPE_DESCRIPTIONS = {
  [LIGHTBOX_EVENT_TYPE.PAGE_LOAD]: 'Experience launches as soon as the page loads',
  [LIGHTBOX_EVENT_TYPE.TIME_ON_PAGE]:
    'Experience launches after a user has spent time on the page, whether total, active, or inactive',
  [LIGHTBOX_EVENT_TYPE.ACTIVE_TIME]:
    'Experience launches after a user has spent a set amount of time actively engaging with the page',
  [LIGHTBOX_EVENT_TYPE.IDLE_TIME]:
    'Experience launches after a user has spent a set amount of time inactive on the page',
  [LIGHTBOX_EVENT_TYPE.SCROLL]:      'Experience launches when a user has scrolled a percentage of the page',
  [LIGHTBOX_EVENT_TYPE.EXIT_INTENT]: 'Experience launches just before a user exits the page',
  [LIGHTBOX_EVENT_TYPE.CLICK]:       'Launch the experience when a user clicks the selected element',
  [LIGHTBOX_EVENT_TYPE.HOVER]:       'Launch the experience when a user hovers over the selected element',
  [LIGHTBOX_EVENT_TYPE.BUTTON]:      'Experience launches when a user clicks a button on the page',
};

const EVENT_TYPE_ICON = {
  [LIGHTBOX_EVENT_TYPE.PAGE_LOAD]:    'page-load',
  [LIGHTBOX_EVENT_TYPE.EXIT_INTENT]:  'exit-intent',
  [LIGHTBOX_EVENT_TYPE.TIME_ON_PAGE]: 'time-on-page',
  [LIGHTBOX_EVENT_TYPE.ACTIVE_TIME]:  'time-on-page',
  [LIGHTBOX_EVENT_TYPE.IDLE_TIME]:    'time-on-page',
  [LIGHTBOX_EVENT_TYPE.SCROLL]:       'scroll',
  [LIGHTBOX_EVENT_TYPE.CLICK]:        'event',
  [LIGHTBOX_EVENT_TYPE.HOVER]:        'event',
  [LIGHTBOX_EVENT_TYPE.BUTTON]:       'button',
};

const EVENT_TYPE_LIST = [
  {
    text:  'Page Load',
    value: LIGHTBOX_EVENT_TYPE.PAGE_LOAD,
    icon:  <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.PAGE_LOAD]} size={enums.sizes.SMALL} />,
  },
  {
    text:  'Button',
    value: LIGHTBOX_EVENT_TYPE.BUTTON,
    icon:  <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.BUTTON]} size={enums.sizes.SMALL} />,
  },

  //action based events
  {
    text:     'Action',
    value:    'action-based-events',
    disabled: true,
    icon:     <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.CLICK]} size={enums.sizes.SMALL} />,
  },
  {
    text:   'Click',
    value:  LIGHTBOX_EVENT_TYPE.CLICK,
    indent: enums.sizes.SMALL,
    icon:   <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.CLICK]} size={enums.sizes.SMALL} />,
  },
  {
    text:   'Hover',
    value:  LIGHTBOX_EVENT_TYPE.HOVER,
    indent: enums.sizes.SMALL,
    icon:   <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.HOVER]} size={enums.sizes.SMALL} />,
  },
  {
    text:   'Scroll Depth',
    value:  LIGHTBOX_EVENT_TYPE.SCROLL,
    indent: enums.sizes.SMALL,
    icon:   <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.SCROLL]} size={enums.sizes.SMALL} />,
  },
  {
    text:   'Exit Intent',
    value:  LIGHTBOX_EVENT_TYPE.EXIT_INTENT,
    indent: enums.sizes.SMALL,
    icon:   <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.EXIT_INTENT]} size={enums.sizes.SMALL} />,
  },

  //time based events
  {
    text:     'Time',
    value:    'time-based-events',
    disabled: true,
    icon:     <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.TIME_ON_PAGE]} size={enums.sizes.SMALL} />,
  },
  {
    text:   'Total Time on Page',
    value:  LIGHTBOX_EVENT_TYPE.TIME_ON_PAGE,
    indent: enums.sizes.SMALL,
    icon:   <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.TIME_ON_PAGE]} size={enums.sizes.SMALL} />,
  },
  {
    text:   'Active Time',
    value:  LIGHTBOX_EVENT_TYPE.ACTIVE_TIME,
    indent: enums.sizes.SMALL,
    icon:   <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.ACTIVE_TIME]} size={enums.sizes.SMALL} />,
  },
  {
    text:   'Inactive Time',
    value:  LIGHTBOX_EVENT_TYPE.IDLE_TIME,
    indent: enums.sizes.SMALL,
    icon:   <LightboxIcon svgIcon={EVENT_TYPE_ICON[LIGHTBOX_EVENT_TYPE.IDLE_TIME]} size={enums.sizes.SMALL} />,
  },
];

const TIME_BASED_EVENT_TYPES = [
  LIGHTBOX_EVENT_TYPE.TIME_ON_PAGE,
  LIGHTBOX_EVENT_TYPE.ACTIVE_TIME,
  LIGHTBOX_EVENT_TYPE.IDLE_TIME,
];

const DOM_BASED_EVENT_TYPES = [LIGHTBOX_EVENT_TYPE.CLICK, LIGHTBOX_EVENT_TYPE.HOVER];

const WEB_EVENT_CATEGORY = {
  PAGE:   'page',
  ACTION: 'action',
  TIME:   'time',
  BUTTON: 'button',
};

const DEFAULT_WEB_EVENT_TYPE = {
  [WEB_EVENT_CATEGORY.PAGE]:   LIGHTBOX_EVENT_TYPE.PAGE_LOAD,
  [WEB_EVENT_CATEGORY.ACTION]: LIGHTBOX_EVENT_TYPE.CLICK,
  [WEB_EVENT_CATEGORY.TIME]:   LIGHTBOX_EVENT_TYPE.TIME_ON_PAGE,
  [WEB_EVENT_CATEGORY.BUTTON]: LIGHTBOX_EVENT_TYPE.BUTTON,
};

const WEB_EVENT_LAUNCH_BYLINE = {
  [LIGHTBOX_EVENT_TYPE.PAGE_LOAD]:    'Lightbox launched on Page Load',
  [LIGHTBOX_EVENT_TYPE.EXIT_INTENT]:  'Lightbox launched on Exit Intent',
  [LIGHTBOX_EVENT_TYPE.TIME_ON_PAGE]: 'Lightbox launched after a set time',
  [LIGHTBOX_EVENT_TYPE.ACTIVE_TIME]:  'Lightbox launched after a set time',
  [LIGHTBOX_EVENT_TYPE.IDLE_TIME]:    'Lightbox launched after a set time',
  [LIGHTBOX_EVENT_TYPE.SCROLL]:       'Lightbox launched on Page Scroll',
  [LIGHTBOX_EVENT_TYPE.CLICK]:        'Lightbox launched on Click',
  [LIGHTBOX_EVENT_TYPE.HOVER]:        'Lightbox launched on Hover',
  [LIGHTBOX_EVENT_TYPE.BUTTON]:       'Lightbox launched on Button Click',
};

const INSERT_METHODS = {
  APPEND:  'append',
  PREPEND: 'prepend',
  BEFORE:  'before',
  AFTER:   'after',
};

const INSERT_METHOD_OPTIONS = [
  { value: INSERT_METHODS.APPEND, text: 'Append' },
  { value: INSERT_METHODS.PREPEND, text: 'Prepend' },
  { value: INSERT_METHODS.BEFORE, text: 'Before' },
  { value: INSERT_METHODS.AFTER, text: 'After' },
];

export default {
  LIGHTBOX_EVENT_TYPE,
  DEFAULT_LIGHTBOX_TRANSPARENCY,
  DEFAULT_OVERLAY_SIZE,
  DEFAULT_INNETWORK_X_DIMENSION,
  DEFAULT_INNETWORK_Y_DIMENSION,
  EVENT_TYPE_LIST,
  DOM_BASED_EVENT_TYPES,
  OVERLAY_SIZES,
  OVERLAY_SIZE_LIST,
  OVERLAY_POSITIONS,
  DEFAULT_OVERLAY_POSITION,
  EVENT_TYPE_DESCRIPTIONS,
  TIME_BASED_EVENT_TYPES,
  WEB_EVENT_CATEGORY,
  DEFAULT_WEB_EVENT_TYPE,
  WEB_EVENT_LAUNCH_BYLINE,
  EVENT_TYPE_ICON,
  INSERT_METHODS,
  INSERT_METHOD_OPTIONS,
};
